import { TraceCategory } from '@sternum/web/features/monitoring/models';
import { User } from '@sternum/web/features/user/models';
import { TRPCRouterOutputs } from '@sternum/web/services/trpc';

import { AlertSeverity, AlertSource } from './alerts.models';

export type AlertRuleId = string;

export enum AlertRulesSortDirection {
  Asc = 'ASC',
  Desc = 'DESC',
}

export enum AlertRuleType {
  'WhiteList' = 'WHITE_LIST',
  'PatternViolation' = 'PATTERN_VIOLATION',
  'UnknownBehavior' = 'UNKNOWN_BEHAVIOUR',
}

export interface AlertRuleProduct {
  id: string;
  name: string;
  createdAt: string;
  updatedAt?: string;
}

export interface AlertRuleProductVersion {
  id: string;
  name: string;
  version: number;
  firmwareVersion: string;
  environment: 'PRODUCTION' | 'STAGING';
  productId: string;
}

export interface AlertRule {
  id: AlertRuleId;
  name: string;
  description: string;
  type: AlertRuleType;
  category: TraceCategory;
  severity: AlertSeverity;
  traceDefinitionId: string;
  createdAt: Date;
  updatedAt: Date | null;
  isUsingAllProducts: boolean;
  products: AlertRuleProduct[];
  product: AlertRuleProduct;
  productVersion: AlertRuleProductVersion;
  definition: AlertRuleDefinition;
  trace: AlertRuleTraceApi;
  updaterUser?: Pick<User, 'id' | 'firstName' | 'secondName' | 'colorCode'> | null;
  isEnabled: boolean;
  source?: AlertSource;
}

export interface AlertRuleDefinitionUnknownBehavior {
  alertRuleType: AlertRuleType.UnknownBehavior;
  traceDefinitionId: string;
  argumentDefinitionId: string;
  ignoreValues: string[];
}

export interface AlertRuleDefinitionTraceQuery {
  queryType: 'AND' | 'OR';
  filters: {
    argumentDefinitionId: string;
    filterCondition: string;
    value: string | boolean;
  }[];
  queries: AlertRuleDefinitionTraceQuery[];
}

export interface AlertRuleDefinitionPatternViolation {
  alertRuleType: AlertRuleType.PatternViolation;
  traceDefintionId?: string;
  timeAgo?: number;
  timeUnit: AlertRuleTimeUnit[number];
  threshold: number;
  traceQuery: AlertRuleDefinitionTraceQuery | null;
  definitionKey?: string;
  alertTriggerKey?: string | null;
  timeAgoSeconds?: number;
  expirationSeconds?: number;
  // eslint-disable-next-line
  eventsAggregationDefinition?: any;
}

export interface WhiteListItem {
  value: string;
  timestamp: number;
  isFromHistoricData: boolean;
}

export interface AlertRuleDefinitionWhiteList {
  alertRuleType: AlertRuleType.WhiteList;
  traceDefinitionId?: string;
  argumentDefinitionId?: string;
  ignoreValues?: string[];
  whiteList?: WhiteListItem[];
}

export type AlertRuleDefinition =
  | AlertRuleDefinitionUnknownBehavior
  | AlertRuleDefinitionPatternViolation
  | AlertRuleDefinitionWhiteList
  | null;

export interface AlertRuleTraceApi {
  id: string;
  displayName: string;
}

export type AlertRuleApi = TRPCRouterOutputs['alertRule']['getAlertsRules']['alertRules'][0];

export enum AlertRuleTimeUnit {
  Seconds = 'SECOND',
  Minutes = 'MINUTE',
  Hours = 'HOUR',
}
